import {
	FC, useRef, useState, useEffect,
} from 'react';
import SpotifyBreadcrumbs from 'wb-spotify/app/routes/superGrid/actionRow/breadcrumbs/breadcrumbs';
import SpotifyFiltersContainer from 'wb-spotify/app/routes/superGrid/actionRow/filters/container';
import {
	ToggleButton,
	SortTool,
	useUrlParams,
	Tooltip,
	DropdownDatePicker,
	DATE_PARSE_FORMAT,
	DatePickerValue,
	useClickOutside,
} from '@aleph/shared-components';
import classNames from 'classnames';
// import RedditNotifications from 'wb-reddit/app/common/components/notifications';
import dayjs from 'dayjs';
import { GridApi } from 'ag-grid-community';
import SpotifyChartsContainer from 'wb-spotify/app/routes/superGrid/actionRow/charts/container';
import { useNavigate, useParams } from 'react-router-dom';
import {
	DEFAULT_SUPERGRID_MAX_DATE,
	DEFAULT_SUPERGRID_MIN_DATE,
	isSupergridDateRangeValid,
	MAX_SUPERGRID_ALLOWED_DATE,
	MIN_SUPERGRID_ALLOWED_DATE,
} from 'wb-spotify/app/routes/superGrid/utils';

const RedditActionRow: FC<{ gridApi: GridApi | null }> = ({ gridApi }) => {
	const navigate = useNavigate();
	const { params, setUrlParam } = useUrlParams({ navigate, useParams });

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const divRef = useRef<HTMLDivElement | null>(null);

	const [filtersOpen, setFiltersOpen] = useState(params?.filters ? !!Object.values(params.filters)?.length : false);
	const [filterButtonPos, setFilterButtonPos] = useState<number>(0);
	const [selectedDateRange, setSelectedDateRange] = useState<DatePickerValue>(
		() => {
			const dateInParams = params?.date?.split(':') ?? [];

			if (isSupergridDateRangeValid(dayjs(dateInParams[0]), dayjs(dateInParams[1]))) {
				return { from: DEFAULT_SUPERGRID_MIN_DATE, to: DEFAULT_SUPERGRID_MAX_DATE };
			}

			return { from: dayjs(dateInParams[0]), to: dayjs(dateInParams[1]) };
		},
	);

	const filtersContainerRef = useClickOutside(() => {
		if (Object.keys(params.filters ?? {}).length === 0) {
			setFiltersOpen(false);
		}
	});

	const filtersCount = Object.keys(params?.filters ?? {}).length;

	const onDateRangeChange = (newRange: DatePickerValue) => {
		setSelectedDateRange(newRange);

		if (!newRange) {
			return;
		}

		setUrlParam(
			'date',
			`${dayjs(newRange.from).format(DATE_PARSE_FORMAT)}:${dayjs(newRange.to).format(DATE_PARSE_FORMAT)}`,
		);
	};

	useEffect(() => {
		if (wrapperRef.current && divRef.current) {
			const wrapperRect = wrapperRef.current.getBoundingClientRect();
			const buttonRect = divRef.current.getBoundingClientRect();

			setFilterButtonPos(buttonRect.left - wrapperRect.left);
		}
	}, [filtersOpen]);

	useEffect(() => {
		if (!filtersOpen) {
			setUrlParam('filters', { });
		}
	}, [filtersOpen]);

	useEffect(() => {
		if (Object.values(params?.filters ?? {})?.length > 0) {
			setFiltersOpen(true);
		}
	}, [JSON.stringify(params?.filters ?? {})]);

	return (
		<div
			className="min-w-0 relative"
		>
			<div
				className="h-[65px] py-[8px] items-center text-[16px]
					flex justify-between gap-[16px] border-b border-gray-950 whitespace-nowrap min-w-0"
				ref={wrapperRef}
			>
				<div className="min-w-0">
					<SpotifyBreadcrumbs />
				</div>

				<div
					className="flex gap-[24px] items-center"
					ref={divRef}
				>
					<Tooltip
						delay={500}
						delayFade={500}
						placement="left"
						content="Discard filters"
						disabled={filtersCount === 0}
					>
						<ToggleButton
							id="filters-button"
							data-testid="filters-button"
							leadingIcon={<SortTool />}
							className={
								classNames(
									'h-[40px] w-[40px] flex items-center justify-center p-0',
									{
										'!text-blue': filtersOpen,
									},
								)
							}
							onClick={(e) => {
								e.stopPropagation();
								setFiltersOpen(!filtersOpen);
							}}
							active={filtersOpen}
						/>
					</Tooltip>

					<DropdownDatePicker
						range
						onChange={onDateRangeChange}
						minDate={MIN_SUPERGRID_ALLOWED_DATE}
						maxDate={MAX_SUPERGRID_ALLOWED_DATE}
						maxYear={MAX_SUPERGRID_ALLOWED_DATE.year()}
						minYear={MIN_SUPERGRID_ALLOWED_DATE.year()}
						value={selectedDateRange}
						pressEnterToConfirm
					/>

					{/* <RedditNotifications /> */}
				</div>
			</div>

			<SpotifyFiltersContainer
				filtersOpen={filtersOpen}
				setFiltersOpen={setFiltersOpen}
				filterButtonPos={filterButtonPos}
				ref={filtersContainerRef}
			/>

			<SpotifyChartsContainer
				gridApi={gridApi}
			/>
		</div>
	);
};

export default RedditActionRow;
